import { useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Wallet, Calculator, Store, ShoppingCart, FileText, Save, Loader2,
  Plus, Minus, Trash2, Copy, Search, Upload, Paperclip, AlertCircle, Building2, User,
  ChevronDown, Check, ChevronLeft, ChevronRight, RefreshCw, Star, Pencil,
} from 'lucide-react'
import { apiGet, postJson, patchJson } from '../../lib/api.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { uploadExpenseFile, periodFolder } from '../../lib/expenseUpload.js'
import {
  computeAmount, fmtMoney, billingTotal, TH_MONTHS_ABBR, WHT_PRESETS, FILE_TAGS, xanoMessage,
} from '../../lib/expense.js'
import SellerEditor from './SellerEditor.jsx'
import AssetPicker from '../asset/AssetPicker.jsx'
import ScanIntake from './ScanIntake.jsx'
import OcrReviewDialog, { ScanButton } from './OcrReviewDialog.jsx'

/* ── shared field bits ─────────────────────────────────────────────────────── */
const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 transition'
const LABEL = 'block text-[12px] font-medium text-slate-500 mb-1'

function Field({ label, children }) {
  return <label className="block"><span className={LABEL}>{label}</span>{children}</label>
}

function Switch({ on, onChange, label, hint }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition w-full ${on ? 'border-brand-300 bg-brand-50/60' : 'border-slate-200 bg-white hover:border-slate-300'}`}
    >
      <span className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition ${on ? 'bg-brand-600' : 'bg-slate-300'}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
      </span>
      <span className="min-w-0">
        <span className={`block text-[13px] font-medium ${on ? 'text-brand-700' : 'text-slate-600'}`}>{label}</span>
        {hint && <span className="block text-[11px] text-slate-400 leading-tight">{hint}</span>}
      </span>
    </button>
  )
}

function SectionHead({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-3.5">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><Icon size={16} strokeWidth={2} /></span>
        <h3 className="text-[14.5px] font-semibold text-slate-800">{title}</h3>
      </div>
      {right}
    </div>
  )
}

/* ── seller autocomplete (queries /api/sellers live) ───────────────────────── */
function SellerSearch({ onPick }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  // Keyboard nav: only the real seller rows are indexable (rendered when !loading).
  const count = loading ? 0 : rows.length
  const ta = useTypeahead({
    count,
    onSelect: (i) => { const s = rows[i]; if (!s) return; onPick(s); setOpen(false); setQ('') },
    onClose: () => setOpen(false),
  })

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => {
    if (!q.trim()) { setRows([]); return }
    let alive = true
    setLoading(true)
    const t = setTimeout(() => {
      apiGet('/api/sellers', { search: q.trim(), per_page: 20 })
        .then((r) => { if (alive) setRows(Array.isArray(r) ? r : (r?.items || [])) })
        .catch(() => { if (alive) setRows([]) })
        .finally(() => { if (alive) setLoading(false) })
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [q])

  return (
    <div className="relative" ref={ref}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={ta.onKeyDown}
        placeholder="ค้นหาบริษัท / ผู้ขายที่มีอยู่…"
        className={`${FIELD} pl-9`}
      />
      {open && q.trim() && (
        <div ref={ta.listRef} className="absolute z-30 mt-1.5 w-full max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 py-1">
          {loading && <div className="px-3 py-2 text-[13px] text-slate-400"><Loader2 size={13} className="inline animate-spin mr-1.5" /> กำลังค้นหา…</div>}
          {!loading && !rows.length && <div className="px-3 py-2 text-[13px] text-slate-400">ไม่พบ — พิมพ์ในช่อง “ชื่อบริษัท” เพื่อสร้างใหม่</div>}
          {!loading && rows.map((s, i) => {
            const name = s.seller_name || s.name || '(ไม่มีชื่อ)'
            const taxid = s.seller_taxid || s.taxid || s.tax_id || ''
            return (
              <button
                key={s.id ?? s.seller_id ?? name}
                type="button"
                data-ti-index={i}
                onMouseEnter={() => ta.setActiveIndex(i)}
                onMouseDown={(e) => { e.preventDefault(); onPick(s); setOpen(false); setQ('') }}
                className={`flex w-full items-center justify-between gap-2 text-left px-3 py-2 transition ${i === ta.activeIndex ? 'bg-brand-50' : 'hover:bg-brand-50'}`}
              >
                <span className="text-[14px] text-slate-700 truncate">{name}</span>
                {taxid && <span className="text-[11.5px] text-slate-400 tabular-nums shrink-0">{taxid}</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* Error codes from /api/expense-ocr that carry no user-facing `detail`. */
const OCR_ERRORS = {
  ocr_not_configured: 'ยังไม่ได้ตั้งค่า ANTHROPIC_API_KEY บน Pages',
  wasabi_read_failed: 'อ่านไฟล์จากที่เก็บไม่สำเร็จ',
  ocr_empty: 'อ่านเอกสารไม่ได้ — ลองถ่ายใหม่ให้ชัดขึ้น',
  ocr_bad_json: 'ผลลัพธ์ไม่ถูกต้อง — ลองใหม่อีกครั้ง',
  ocr_no_document: 'ไม่พบเอกสารในไฟล์นี้ — ตรวจสอบว่าแนบไฟล์ถูกต้อง',
  ocr_upstream: 'บริการอ่านเอกสารขัดข้อง — ลองใหม่อีกครั้ง',
}

/* ── the editor ────────────────────────────────────────────────────────────── */
const emptyForm = () => {
  const t = new Date()
  return {
  detail: '',
  branch_id: '',
  date_buy: toISOToday(),
  // accounting period (รอบบัญชี) — defaults to the buy month but editable on its own
  expense_month_num: t.getMonth() + 1,
  expense_year: t.getFullYear(),
  paid: false,
  tax: true,
  petty_cash: false,
  asset: false,
  asset_id: 0,          // linked สินทรัพย์ record (when asset=true) — the reverse of asset.expense_id
  asset_display: '',    // transient label for the linked asset (never sent to the API)
  recurring: false,     // "ทำซ้ำทุกเดือน" — appears in the monthly to-record checklist
  recurring_id: 0,      // series key (= the anchor record's id) shared by every month's instance
  remark: '',
  // amount inputs
  price: '',
  discount: '',
  VAT_add: false,
  VAT_include: false,
  WHT_add: false,
  WHT_percent: 3,
  WHT_type: 'ค่าบริการ',
  // seller
  seller_id: 0,
  seller_name: '',
  seller_type: 'นิติบุคคล',
  seller_taxid: '',
  seller_phone: '',
  seller_address: '',
  }
}

export default function ExpenseEditor({ mode, expenseId, branchName, onClose, onSaved, onCopied, onPrev, onNext, hasPrev = false, hasNext = false, navPos = null, changeSignal = 0, kind = 'expense' }) {
  const isTpl = kind === 'recurring'                                   // editing a favorite template (expense_recurring)
  const apiBase = isTpl ? '/api/expense-recurring/' : '/api/expense/'
  const [active, setActive] = useState('detail')     // which section is in view (jump-nav highlight)
  const scrollRef = useRef(null)
  const secDetail = useRef(null)
  const secBilling = useRef(null)
  const secFiles = useRef(null)
  const secRefs = { detail: secDetail, billing: secBilling, files: secFiles }
  const [form, setForm] = useState(emptyForm)
  const [billing, setBilling] = useState([])
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(mode === 'edit')
  const [saving, setSaving] = useState(false)
  const [acting, setActing] = useState(null)          // 'copy' | 'delete' — record-level actions
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)
  const [error, setError] = useState(null)
  const [stale, setStale] = useState(false)       // another user changed THIS record
  const [reloadNonce, setReloadNonce] = useState(0)
  const [addingFav, setAddingFav] = useState(false)
  // OCR: which attachment is being read, the result awaiting review, and which
  // attachments have already been read (so the button reads "อ่านแล้ว").
  const [ocrBusy, setOcrBusy] = useState(null)      // file location currently scanning
  const [ocrResult, setOcrResult] = useState(null)  // { extracted, seller_match, secs }
  const [ocrDone, setOcrDone] = useState(() => new Set())
  const [ocrError, setOcrError] = useState(null)
  // The attach-first panel leads a NEW expense; hidden once the user opts to type it by hand.
  const [intakeHidden, setIntakeHidden] = useState(false)
  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  /** Read one attachment. The bytes stay server-side — we only send the key. */
  async function runOcr(location) {
    if (!location || ocrBusy) return
    setOcrBusy(location); setOcrError(null)
    try {
      const res = await postJson('/api/expense-ocr', { location })
      setOcrResult(res)
      setOcrDone((s) => new Set(s).add(location))
    } catch (e) {
      // The endpoint answers { error, detail } — `detail` is already Thai for the
      // cases a user can act on, so prefer it over the generic HTTP text.
      const code = e?.body?.error
      setOcrError(e?.body?.detail || OCR_ERRORS[code] || xanoMessage(e) || 'อ่านเอกสารไม่สำเร็จ')
    } finally {
      setOcrBusy(null)
    }
  }

  /**
   * Apply only the groups the user ticked in the review dialog.
   *
   * ⚠️ APPEND IS THE DEFAULT, AND IT HAS TO BE. One expense legitimately holds several
   * invoices — a second file read into the same record must ADD its lines, not wipe the
   * first invoice's. The dialog has already dropped any line this record already holds
   * (see splitNewRows), so re-reading the same document still changes nothing; that is
   * what the old blanket replace was really protecting against, and it cost every
   * multi-invoice record its earlier lines to do it. `replace` only arrives when the
   * user picks แทนที่ทั้งหมด.
   */
  function applyOcr({ patch, billing: rows, mode }) {
    if (patch && Object.keys(patch).length) set(patch)
    if (rows?.length) setBilling((cur) => (mode === 'replace' ? rows : [...cur, ...rows]))
    setOcrResult(null)
  }

  // Load existing record for edit.
  useEffect(() => {
    if (mode !== 'edit' || !expenseId) return
    let alive = true
    setLoading(true)
    apiGet(apiBase + expenseId)
      .then((e) => {
        if (!alive || !e) return
        const a = e.amount || {}
        const s = e.seller || {}
        const db = (e.date_buy || e.date_display || '').slice(0, 10) || toISOToday()
        setForm({
          detail: e.detail || '',
          branch_id: e.branch_id ? String(e.branch_id) : '',
          date_buy: db,
          expense_month_num: Number(e.expense_month_num) || (Number(db.slice(5, 7)) || 1),
          expense_year: Number(e.expense_year) || (Number(db.slice(0, 4)) || new Date().getFullYear()),
          paid: !!e.paid,
          tax: !!e.tax,
          petty_cash: !!e.petty_cash,
          asset: !!e.asset,
          asset_id: Number(e.asset_id) || 0,
          asset_display: e.asset_id > 0 ? `สินทรัพย์ #${e.asset_id}` : '',
          recurring: !!e.recurring,
          recurring_id: Number(e.recurring_id) || 0,
          remark: e.remark || '',
          price: a.total_price != null ? String(a.total_price) : '',
          discount: a.discount != null && a.discount !== 0 ? String(a.discount) : '',
          VAT_add: !!a.VAT_add,
          VAT_include: !!a.VAT_include,
          WHT_add: !!a.WHT_add,
          WHT_percent: a.WHT_percent || 3,
          WHT_type: a.WHT_type || '',
          seller_id: s.seller_id || 0,
          seller_name: s.seller_name || '',
          seller_type: s.seller_type || 'นิติบุคคล',
          seller_taxid: s.seller_taxid || '',
          seller_phone: s.seller_phone || '',
          seller_address: s.seller_address || '',
        })
        // Legacy rows stored only ราคา/หน่วย — backfill รวม so the (now editable)
        // total column isn't blank on them.
        setBilling(Array.isArray(e.billing) ? e.billing.map((b) => ({
          ...b,
          price_total: Number(b?.price_total) || Math.round(((Number(b?.price) || 0) * (Number(b?.N) || 0) + Number.EPSILON) * 100) / 100,
        })) : [])
        setFiles(Array.isArray(e.file) ? e.file : [])
      })
      .catch((err) => { if (alive) setError('โหลดข้อมูลไม่สำเร็จ: ' + (err.message || err)) })
      .finally(() => { if (alive) { setLoading(false); setStale(false) } })
    return () => { alive = false }
  }, [mode, expenseId, reloadNonce])

  // ราคา/ยอดก่อนหักลด is now driven by the billing lines (sum of the typed รวม column),
  // not typed by hand — so it always reconciles with the รายการซื้อ table.
  const billSum = useMemo(() => billingTotal(billing), [billing])

  // Live amount computation.
  const amount = useMemo(() => computeAmount({
    price: billSum,
    discount: Number(form.discount) || 0,
    VAT_add: form.VAT_add,
    VAT_include: form.VAT_include,
    WHT_add: form.WHT_add,
    WHT_percent: form.WHT_percent,
    WHT_type: form.WHT_type,
  }), [billSum, form.discount, form.VAT_add, form.VAT_include, form.WHT_add, form.WHT_percent, form.WHT_type])

  // Esc closes; ←/→ navigate prev/next (unless typing in a field).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) { onClose?.(); return }
      if (saving) return
      const tag = (e.target?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return
      if (e.key === 'ArrowLeft' && hasPrev) { e.preventDefault(); onPrev?.() }
      else if (e.key === 'ArrowRight' && hasNext) { e.preventDefault(); onNext?.() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, saving, hasPrev, hasNext, onPrev, onNext])

  // Navigating to another record → reset the scroll + active tab + stale flag.
  useEffect(() => {
    if (mode !== 'edit') return
    setActive('detail')
    setStale(false)
    scrollRef.current?.scrollTo({ top: 0 })
  }, [expenseId, mode])

  // Parent bumps `changeSignal` when another user edits THIS open record → offer reload.
  // Compare against the last value seen, not a one-shot "first run" flag: the flag is a
  // ref, so it survives an effect that mounts twice (StrictMode) while state does not —
  // the second run then read as a change and flagged a freshly-opened form stale. Also
  // scoped to edit mode: a create form has no saved record to be stale against.
  const seenSignal = useRef(changeSignal)
  useEffect(() => {
    if (changeSignal === seenSignal.current) return
    seenSignal.current = changeSignal
    if (mode === 'edit') setStale(true)
  }, [changeSignal, mode])

  const reloadRecord = () => { setStale(false); setReloadNonce((n) => n + 1) }

  // Scroll-spy: highlight the jump-nav item for whichever section is near the top.
  useEffect(() => {
    const root = scrollRef.current
    if (!root || loading) return
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting)
      if (!visible.length) return
      visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      const key = visible[0].target.dataset.section
      if (key) setActive(key)
    }, { root, rootMargin: '0px 0px -65% 0px', threshold: 0 })
    ;[secDetail, secBilling, secFiles].forEach((r) => r.current && obs.observe(r.current))
    return () => obs.disconnect()
  }, [loading])

  function jump(key) {
    secRefs[key]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }


  async function save() {
    setError(null)
    if (!form.detail.trim()) { jump('detail'); setError('กรุณากรอกรายละเอียดค่าใช้จ่าย'); return }
    if (!form.branch_id) { jump('detail'); setError('กรุณาเลือกสาขา'); return }
    setSaving(true)
    // รอบบัญชี is edited on its own (defaults to the buy month, but overridable)
    const mNum = Number(form.expense_month_num) || 1
    const period = {
      expense_year: Number(form.expense_year) || new Date().getFullYear(),
      expense_month_num: mNum,
      expense_month: TH_MONTHS_ABBR[mNum - 1] || '',
    }
    const seller = {
      seller_id: form.seller_id || 0,
      seller_name: form.seller_name.trim(),
      seller_type: form.seller_type,
      seller_taxid: form.seller_taxid.trim(),
      seller_phone: form.seller_phone.trim(),
      seller_address: form.seller_address.trim(),
    }
    // Favorite link (recurring_id → expense_recurring template) is set by the
    // "เพิ่มในรายการโปรด" button; here we just persist the current link state.
    const recurringId = Number(form.recurring_id) || 0
    const isFav = recurringId > 0
    // Fields common to create + edit. Audit fields (created_by/modified_by/
    // modified_at) are stamped server-side by the proxy.
    const core = {
      detail: form.detail.trim(),
      branch_id: form.branch_id ? Number(form.branch_id) : 0,
      date_buy: form.date_buy,
      date_display: form.date_buy,
      ...period,
      amount,
      remark: form.remark.trim(),
      paid: form.paid,
      tax: form.tax,
      asset: form.asset,
      petty_cash: form.petty_cash,
      recurring: isFav,
      recurring_id: recurringId, // keep the link even when unstarred → no duplicate on re-enable
      seller,
      billing,
      file: files,
    }
    // Link/unlink the asset (the reverse of asset.expense_id). Only real expenses
    // carry this column; recurring templates don't. Unticking สินทรัพย์ clears it.
    if (!isTpl) core.asset_id = form.asset ? (Number(form.asset_id) || 0) : 0
    let savedId = mode === 'edit' ? expenseId : 0
    try {
      if (mode === 'edit') {
        // PATCH picks the keys we send; it persists asset/petty_cash/amount/asset_id/… .
        await patchJson(apiBase + expenseId, core)
      } else {
        // Create via POST (no id) — the Xano create path ignores asset,
        // petty_cash & asset_id, so PATCH them onto the new row when set.
        const created = await postJson('/api/expense', { ...core, active: true, unique_id: uid() })
        const newId = created?.id ?? created?.expense?.id
        savedId = newId || 0
        const followUp = {}
        if (form.petty_cash || form.asset) { followUp.petty_cash = form.petty_cash; followUp.asset = form.asset }
        if (!isTpl && form.asset && (Number(form.asset_id) || 0) > 0) followUp.asset_id = Number(form.asset_id)
        if (newId && Object.keys(followUp).length) await patchJson('/api/expense/' + newId, followUp)
      }
      // Reverse-link the asset to this purchase (best-effort) so asset.expense_id and
      // expense.asset_id stay in step — mirrors AssetMaintenance's reverse link.
      if (!isTpl && form.asset && (Number(form.asset_id) || 0) > 0 && savedId) {
        patchJson('/api/assets/' + Number(form.asset_id), { expense_id: savedId }).catch(() => {})
      }
      onSaved?.()
    } catch (e) {
      setError('บันทึกไม่สำเร็จ: ' + (e.message || e))
      setSaving(false)
    }
  }

  // "เพิ่มในรายการโปรด" — copy this expense into expense_recurring (a template) and
  // link it (recurring_id). One-way action; removal happens from the รายการโปรด drawer.
  async function addToFav() {
    if (!form.detail.trim()) { jump('detail'); setError('กรอกรายละเอียดก่อนเพิ่มในรายการโปรด'); return }
    setError(null); setAddingFav(true)
    try {
      const mNum = Number(form.expense_month_num) || 1
      const tpl = await postJson('/api/expense-recurring', {
        detail: form.detail.trim(),
        branch_id: form.branch_id ? Number(form.branch_id) : 0,
        date_buy: form.date_buy, date_display: form.date_buy,
        expense_year: Number(form.expense_year) || new Date().getFullYear(),
        expense_month_num: mNum, expense_month: TH_MONTHS_ABBR[mNum - 1] || '',
        amount,
        remark: form.remark.trim(),
        tax: form.tax, asset: form.asset, petty_cash: form.petty_cash,
        seller: {
          seller_id: form.seller_id || 0, seller_name: form.seller_name.trim(), seller_type: form.seller_type,
          seller_taxid: form.seller_taxid.trim(), seller_phone: form.seller_phone.trim(), seller_address: form.seller_address.trim(),
        },
        // strip per-expense reference codes (invoice numbers) — a reusable template shouldn't carry them
        billing: (billing || []).map((b) => ({ ...b, ref: '' })),
        file: [], active: true,
      })
      const tid = tpl?.id ?? tpl?.expense_recurring?.id ?? 0
      if (!tid) throw new Error('ไม่ได้รับรหัสรายการโปรด')
      set({ recurring: true, recurring_id: tid })
      // persist the link on an existing record right away (new records link on save)
      if (mode === 'edit' && expenseId) { try { await patchJson('/api/expense/' + expenseId, { recurring: true, recurring_id: tid }) } catch { /* saved on next บันทึก */ } }
    } catch (e) {
      setError('เพิ่มในรายการโปรดไม่สำเร็จ: ' + (e.message || e))
    } finally { setAddingFav(false) }
  }

  // Duplicate this expense into the current month.
  // Copy this expense into a chosen period/branch (dialog picks month/year/branch).
  async function doCopy(target) {
    setError(null); setActing('copy')
    try {
      const mNum = Number(target.month) || (new Date().getMonth() + 1)
      const created = await postJson('/api/expense/copy', {
        id: expenseId,
        year: Number(target.year) || new Date().getFullYear(),
        month_num: mNum,
        month: TH_MONTHS_ABBR[mNum - 1] || '',
        branch_id: target.branch ? Number(target.branch) : undefined,
      })
      const newId = created?.id ?? created?.expense?.id ?? null
      // ensure the copy lands in the chosen branch (copy keeps source branch by default)
      if (newId && target.branch && String(target.branch) !== String(form.branch_id)) {
        try { await patchJson('/api/expense/' + newId, { branch_id: Number(target.branch) }) } catch { /* non-fatal */ }
      }
      setCopyOpen(false)
      onCopied?.(newId, { year: Number(target.year), month: mNum, branch: target.branch ? String(target.branch) : '' })
    } catch (e) {
      setError('คัดลอกไม่สำเร็จ: ' + (e.message || e)); setActing(null)
    }
  }

  async function doDelete() {
    setError(null); setActing('delete'); setConfirmDelete(false)
    try {
      const res = await fetch(apiBase + expenseId, { method: 'DELETE', credentials: 'include' })
      if (!res.ok) throw new Error('HTTP ' + res.status)
      onSaved?.()
    } catch (e) {
      setError('ลบไม่สำเร็จ: ' + (e.message || e)); setActing(null)
    }
  }

  const busy = saving || !!acting

  const tabs = [
    { k: 'detail', label: 'รายละเอียด', icon: Wallet },
    { k: 'billing', label: 'รายการซื้อ', icon: ShoppingCart, count: billing.length },
    // files are disabled for favorite templates
    ...(isTpl ? [] : [{ k: 'files', label: 'ไฟล์', icon: FileText, count: files.length }]),
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !saving && onClose?.()} />
      <div className="relative w-full max-w-[1100px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">
        {/* Header + tabs */}
        <div className="flex items-center justify-between px-6 pt-4 bg-white border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 text-[16px] font-semibold text-slate-900">
              {isTpl ? <Star size={18} className="text-amber-400 fill-amber-400" /> : <Wallet size={18} className="text-brand-600" />}
              {isTpl ? 'แก้ไขรายการโปรด' : (mode === 'edit' ? 'แก้ไขค่าใช้จ่าย' : 'เพิ่มค่าใช้จ่าย')}
              {mode === 'edit' && expenseId && <span className="text-[12px] font-normal text-slate-400">#{expenseId}</span>}
            </div>
            <div className="flex gap-1 mt-3 -mb-px">
              {tabs.map((t) => (
                <button
                  key={t.k}
                  onClick={() => jump(t.k)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-[13.5px] font-medium border-b-2 transition ${active === t.k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                  <t.icon size={15} /> {t.label}
                  {t.count != null && t.count > 0 && <span className="rounded-full bg-slate-100 px-1.5 text-[11px] text-slate-500">{t.count}</span>}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1 self-start mt-1">
            {mode === 'edit' && navPos && (
              <>
                <button
                  onClick={() => hasPrev && onPrev?.()}
                  disabled={!hasPrev || busy}
                  title="รายการก่อนหน้า (←)"
                  className="p-1.5 rounded-lg text-slate-500 hover:text-brand-600 hover:bg-brand-50 disabled:opacity-30 disabled:pointer-events-none"
                ><ChevronLeft size={18} /></button>
                <span className="text-[12px] text-slate-400 tabular-nums px-0.5 select-none">{navPos.index + 1}/{navPos.total}</span>
                <button
                  onClick={() => hasNext && onNext?.()}
                  disabled={!hasNext || busy}
                  title="รายการถัดไป (→)"
                  className="p-1.5 rounded-lg text-slate-500 hover:text-brand-600 hover:bg-brand-50 disabled:opacity-30 disabled:pointer-events-none"
                ><ChevronRight size={18} /></button>
                <span className="w-px h-5 bg-slate-200 mx-1" />
              </>
            )}
            {mode === 'edit' && (
              <>
                {!isTpl && (
                  <button
                    onClick={() => setCopyOpen(true)}
                    disabled={busy}
                    title="คัดลอกรายการนี้ไปเดือน/สาขาที่เลือก"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium text-slate-500 hover:text-brand-600 hover:bg-brand-50 disabled:opacity-50"
                  >
                    {acting === 'copy' ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} คัดลอก
                  </button>
                )}
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  title="ลบรายการนี้"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium text-slate-500 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                >
                  {acting === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} ลบ
                </button>
                <span className="w-px h-5 bg-slate-200 mx-1" />
              </>
            )}
            <button onClick={() => !busy && onClose?.()} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        {/* Another user edited this record while it's open — offer a non-destructive reload. */}
        {stale && !loading && (
          <div className="flex items-center justify-between gap-3 px-6 py-2.5 bg-amber-50 border-b border-amber-200">
            <span className="flex items-center gap-2 text-[13px] text-amber-800">
              <AlertCircle size={15} /> มีการแก้ไขรายการนี้จากผู้ใช้อื่น — ข้อมูลอาจไม่ใช่ล่าสุด
            </span>
            <button
              onClick={reloadRecord}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            ><RefreshCw size={13} /> โหลดใหม่</button>
          </div>
        )}

        {/* Body — all sections on one scroll surface */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-400"><Loader2 size={20} className="animate-spin mr-2" /> กำลังโหลด…</div>
          ) : (
            <div className="space-y-6">
              {mode === 'create' && !isTpl && !intakeHidden && (
                <ScanIntake
                  form={form} files={files} setFiles={setFiles}
                  onScan={runOcr} ocrBusy={ocrBusy} ocrDone={ocrDone} ocrError={ocrError}
                  onDismiss={() => setIntakeHidden(true)}
                />
              )}
              <section ref={secDetail} data-section="detail" className="scroll-mt-4">
                <DetailTab form={form} set={set} amount={amount} branchName={branchName} priceBase={billSum} onAddFav={addToFav} addingFav={addingFav} showFav={!isTpl} showAsset={!isTpl} mode={mode} expenseId={expenseId} />
              </section>
              <section ref={secBilling} data-section="billing" className="scroll-mt-4">
                <BillingTab billing={billing} setBilling={setBilling} billSum={billSum}
                  defaultDetail={form.detail} defaultDate={form.date_buy} />
              </section>
              {!isTpl && (
                <section ref={secFiles} data-section="files" className="scroll-mt-4">
                  <FilesTab
                    files={files} setFiles={setFiles} form={form}
                    onScan={runOcr}
                    ocrBusy={ocrBusy} ocrDone={ocrDone} ocrError={ocrError}
                  />
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-3.5 bg-white border-t border-slate-200">
          <div className="flex items-center gap-4">
            <div className="text-[13px] text-slate-500">
              ยอดชำระรวม <b className="text-[16px] text-slate-900 tabular-nums ml-1">{fmtMoney(amount.payment)}</b> บาท
            </div>
            {!isTpl && form.WHT_add && (
              <a
                href={mode === 'edit' && expenseId ? `/expense/wht?id=${expenseId}` : undefined}
                target="_blank" rel="noreferrer"
                onClick={(e) => { if (!(mode === 'edit' && expenseId)) { e.preventDefault(); setError('บันทึกค่าใช้จ่ายก่อน จึงจะพิมพ์หนังสือรับรอง 50 ทวิ ได้') } }}
                title={mode === 'edit' && expenseId ? 'เปิดหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ)' : 'บันทึกก่อนจึงจะพิมพ์ได้'}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[13px] font-medium ${mode === 'edit' && expenseId ? 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100' : 'border-slate-200 text-slate-400 cursor-not-allowed'}`}
              >
                <FileText size={14} /> พิมพ์ 50 ทวิ
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            {error && <span className="text-[12.5px] text-rose-600 flex items-center gap-1"><AlertCircle size={14} /> {error}</span>}
            {!error && !form.branch_id && <span className="text-[12.5px] text-amber-600 flex items-center gap-1"><AlertCircle size={14} /> เลือกสาขาก่อนบันทึก</span>}
            <button onClick={() => !busy && onClose?.()} className="px-4 py-2 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
            <button
              onClick={save}
              disabled={busy || !form.branch_id}
              title={!form.branch_id ? 'กรุณาเลือกสาขาก่อนบันทึก' : undefined}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-[14px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} บันทึก
            </button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={isTpl ? 'ลบออกจากรายการโปรด?' : 'ลบรายการค่าใช้จ่าย?'}
          message={isTpl ? 'รายการโปรดนี้จะถูกลบ — ค่าใช้จ่ายที่บันทึกไปแล้วจะยังอยู่' : 'การลบไม่สามารถย้อนกลับได้'}
          confirmLabel="ลบ"
          tone="danger"
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {copyOpen && (
        <CopyDialog
          branchName={branchName}
          defaultBranch={form.branch_id}
          detail={form.detail}
          busy={acting === 'copy'}
          onConfirm={doCopy}
          onCancel={() => setCopyOpen(false)}
        />
      )}

      {ocrResult && (
        <OcrReviewDialog
          result={ocrResult}
          billing={billing}
          form={form}
          onApply={applyOcr}
          onCancel={() => setOcrResult(null)}
        />
      )}
    </div>
  )
}

/* ── Copy-to dialog: pick target เดือน / ปี / สาขา ─────────────────────────────── */
function CopyDialog({ branchName, defaultBranch, detail, busy, onConfirm, onCancel }) {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [branch, setBranch] = useState(defaultBranch ? String(defaultBranch) : '')
  const gy = now.getFullYear()
  const YEARS = [gy + 1, gy, gy - 1, gy - 2, gy - 3]
  const branches = Object.entries(branchName || {}).sort((a, b) => Number(a[0]) - Number(b[0]))
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onMouseDown={() => !busy && onCancel?.()} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 text-[15px] font-semibold text-slate-900 mb-1"><Copy size={17} className="text-brand-600" /> คัดลอกค่าใช้จ่าย</div>
        <p className="text-[12.5px] text-slate-500 mb-4">คัดลอก “{detail || 'รายการนี้'}” ไปยังรอบบัญชี / สาขาที่เลือก</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={LABEL}>เดือน</span>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={FIELD}>
                {TH_MONTHS_ABBR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="block"><span className={LABEL}>ปี (พ.ศ.)</span>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={FIELD}>
                {YEARS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
              </select>
            </label>
          </div>
          <label className="block"><span className={LABEL}>สาขา</span>
            <select value={branch} onChange={(e) => setBranch(e.target.value)} className={FIELD}>
              <option value="">— เลือกสาขา —</option>
              {branches.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={() => !busy && onCancel?.()} className="px-4 py-2 rounded-lg text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
          <button
            onClick={() => onConfirm({ month, year, branch })}
            disabled={busy || !branch}
            title={!branch ? 'เลือกสาขา' : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >{busy ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} คัดลอก</button>
        </div>
      </div>
    </div>
  )
}

/* ── Detail tab ────────────────────────────────────────────────────────────── */
function DetailTab({ form, set, amount, branchName, priceBase = 0, onAddFav, addingFav, showFav = true, showAsset = false, mode, expenseId }) {
  const branches = Object.entries(branchName || {}).sort((a, b) => Number(a[0]) - Number(b[0]))
  // รอบบัญชี year options (BE), always including the current selection.
  const gy = new Date().getFullYear()
  const yset = new Set([gy + 1, gy, gy - 1, gy - 2, gy - 3, gy - 4])
  if (form.expense_year) yset.add(Number(form.expense_year))
  const YEAR_OPTS = [...yset].sort((a, b) => b - a)

  // Register the currently-entered vendor into the central `seller` table, then
  // link this expense's snapshot to it (seller_id). The seller object embedded on
  // the expense stays a frozen point-in-time snapshot; seller_id is just the link.
  const [savingSeller, setSavingSeller] = useState(false)
  const [sellerMsg, setSellerMsg] = useState(null)
  // Seller master editor (bank / contact person). Editing the master doesn't
  // touch this expense's snapshot, so mirror back only the shared fields.
  const [editSeller, setEditSeller] = useState(null)
  async function registerSeller() {
    if (!form.seller_name.trim()) return
    setSavingSeller(true); setSellerMsg(null)
    try {
      const created = await postJson('/api/sellers', {
        name:    form.seller_name.trim(),
        type:    form.seller_type,
        taxid:   form.seller_taxid.trim(),
        phone:   form.seller_phone.trim(),
        address: form.seller_address.trim(),
        unique_id: uid(),
      })
      const newId = created?.id ?? created?.seller?.id
      if (newId) { set({ seller_id: newId }); setSellerMsg({ ok: true, text: 'เพิ่มบริษัทเข้าระบบแล้ว' }) }
      else setSellerMsg({ ok: false, text: 'เพิ่มไม่สำเร็จ' })
    } catch (e) {
      setSellerMsg({ ok: false, text: xanoMessage(e) })
    } finally {
      setSavingSeller(false)
    }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      {/* Left column */}
      <div className="space-y-5">
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <SectionHead icon={Wallet} title="รายละเอียดค่าใช้จ่าย" />
          <div className="space-y-3.5">
            <Field label="รายละเอียด">
              <input value={form.detail} onChange={(e) => set({ detail: e.target.value })} placeholder="เช่น ค่าวัสดุสิ้นเปลือง เดือน ก.ค." className={FIELD} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="สาขา *">
                <select value={form.branch_id} onChange={(e) => set({ branch_id: e.target.value })} className={`${FIELD} ${!form.branch_id ? 'border-amber-300 bg-amber-50/40' : ''}`}>
                  <option value="">— เลือกสาขา —</option>
                  {branches.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </Field>
              <Field label="วันที่ซื้อ">
                <input
                  type="date"
                  value={form.date_buy}
                  onChange={(e) => {
                    const v = e.target.value
                    const patch = { date_buy: v }
                    // keep รอบบัญชี in step with the buy date (still overridable below)
                    if (v) { patch.expense_month_num = Number(v.slice(5, 7)); patch.expense_year = Number(v.slice(0, 4)) }
                    set(patch)
                  }}
                  className={FIELD}
                />
              </Field>
            </div>
            <Field label="รอบบัญชี">
              <div className="grid grid-cols-2 gap-3">
                <select value={form.expense_month_num} onChange={(e) => set({ expense_month_num: Number(e.target.value) })} className={FIELD}>
                  {TH_MONTHS_ABBR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <select value={form.expense_year} onChange={(e) => set({ expense_year: Number(e.target.value) })} className={FIELD}>
                  {YEAR_OPTS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
                </select>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-2.5 pt-1">
              <Switch on={form.paid} onChange={(v) => set({ paid: v })} label="ชำระเงินแล้ว" hint={form.paid ? undefined : 'รอจ่ายรอบเดือน'} />
              <Switch on={form.tax} onChange={(v) => set({ tax: v })} label="ลงภาษี" hint="บันทึกเข้าระบบภาษี (ค่าเริ่มต้น)" />
              <Switch on={form.petty_cash} onChange={(v) => set({ petty_cash: v })} label="จ่ายด้วยกองกลาง" />
              <Switch on={form.asset} onChange={(v) => set({ asset: v })} label="บันทึกเป็นสินทรัพย์" />
              {showFav && (form.recurring_id > 0 ? (
                <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <Star size={16} className="text-amber-400 fill-amber-400 shrink-0" />
                  <span className="text-[13px] font-medium text-amber-700">อยู่ในรายการโปรด</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onAddFav}
                  disabled={addingFav}
                  className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left hover:border-amber-300 hover:bg-amber-50/50 transition disabled:opacity-60"
                >
                  {addingFav ? <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" /> : <Star size={16} className="text-slate-400 shrink-0" />}
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-slate-600">เพิ่มในรายการโปรด</span>
                    <span className="block text-[11px] text-slate-400 leading-tight">บันทึกไว้ใช้ซ้ำ (ค่าใช้จ่ายประจำ)</span>
                  </span>
                </button>
              ))}
            </div>
            {/* When flagged สินทรัพย์, link the purchase to its asset register entry
                (the reverse of asset.expense_id). Shown only for real expenses. */}
            {showAsset && form.asset && (
              <div className="pt-1">
                <span className={LABEL}>ผูกกับสินทรัพย์ (ทะเบียนทรัพย์สิน)</span>
                <AssetPicker
                  value={Number(form.asset_id) || 0}
                  display={form.asset_display}
                  onPick={(a) => set({ asset_id: a.id, asset_display: `${a.name || 'สินทรัพย์'} · #${a.id}` })}
                  onClear={() => set({ asset_id: 0, asset_display: '' })}
                />
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-400">เชื่อมกับสินทรัพย์ในทะเบียน เพื่อดูค่าเสื่อม / ประวัติซ่อมได้</span>
                  {mode === 'edit' && expenseId && !(Number(form.asset_id) > 0) && (
                    <a
                      href={`/manager/assets?new=1&expense_id=${expenseId}&name=${encodeURIComponent(form.detail || '')}&price=${encodeURIComponent(amount?.total_price_afterVAT ?? '')}&date=${encodeURIComponent(form.date_buy || '')}`}
                      target="_blank" rel="noreferrer"
                      className="shrink-0 inline-flex items-center gap-1 text-[12px] font-medium text-brand-600 hover:text-brand-700"
                    >
                      <Plus size={12} /> สร้างสินทรัพย์จากใบนี้
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <SectionHead icon={Store} title="บริษัท / ผู้ขาย" right={
            form.seller_id > 0 ? (
              <button
                type="button"
                onClick={() => setEditSeller(form.seller_id)}
                title="แก้ไขข้อมูลบริษัท (ธนาคาร / ผู้ติดต่อ)"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-600 px-2.5 py-1 text-[11.5px] font-medium hover:bg-emerald-100"
              >
                <Check size={12} strokeWidth={2.5} /> บริษัทในระบบ #{form.seller_id}
                <Pencil size={11} strokeWidth={2.5} className="opacity-70" />
              </button>
            ) : form.seller_name.trim() ? (
              <button
                type="button"
                onClick={registerSeller}
                disabled={savingSeller}
                title="เพิ่มบริษัทนี้เข้าระบบเพื่อใช้ซ้ำในครั้งต่อไป"
                className="inline-flex items-center gap-1 rounded-lg border border-brand-200 bg-brand-50 text-brand-700 px-2.5 py-1 text-[12px] font-medium hover:bg-brand-100 disabled:opacity-50"
              >
                {savingSeller ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} เพิ่มเข้าระบบ
              </button>
            ) : null
          } />
          <div className="space-y-3.5">
            <SellerSearch onPick={(s) => { setSellerMsg(null); set({
              seller_id: s.id ?? s.seller_id ?? 0,
              seller_name: s.seller_name || s.name || '',
              seller_type: s.seller_type || s.type || 'นิติบุคคล',
              seller_taxid: s.seller_taxid || s.taxid || s.tax_id || '',
              seller_phone: s.seller_phone || s.phone || '',
              seller_address: s.seller_address || s.address || '',
            }) }} />
            {sellerMsg && (
              <div className={`flex items-center gap-1 text-[12px] ${sellerMsg.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                {sellerMsg.ok ? <Check size={13} /> : <AlertCircle size={13} />} {sellerMsg.text}
              </div>
            )}
            <Field label="ชื่อบริษัท หรือผู้ขาย">
              <input value={form.seller_name} onChange={(e) => { setSellerMsg(null); set({ seller_name: e.target.value, seller_id: 0 }) }} placeholder="ค้นหาด้านบน หรือพิมพ์ชื่อบริษัทใหม่" className={FIELD} />
            </Field>
            <div>
              <span className={LABEL}>ประเภท</span>
              <div className="flex gap-2">
                {[['นิติบุคคล', Building2], ['บุคคลธรรมดา', User]].map(([t, Icon]) => (
                  <button key={t} type="button" onClick={() => set({ seller_type: t })}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-[13px] font-medium transition ${form.seller_type === t ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                    <Icon size={14} /> {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="เลขผู้เสียภาษี">
                <input value={form.seller_taxid} onChange={(e) => set({ seller_taxid: e.target.value })} placeholder="0123456789101" className={FIELD} />
              </Field>
              <Field label="เบอร์โทร">
                <input value={form.seller_phone} onChange={(e) => set({ seller_phone: e.target.value })} placeholder="02-000-0000" className={FIELD} />
              </Field>
            </div>
            <Field label="ที่อยู่">
              <textarea value={form.seller_address} onChange={(e) => set({ seller_address: e.target.value })} rows={2} className={`${FIELD} resize-none`} />
            </Field>
          </div>
        </section>
      </div>

      {/* Right column — the money */}
      <div className="space-y-5">
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <SectionHead icon={Calculator} title="บันทึกตัวเลขค่าใช้จ่าย" />
          <div className="grid grid-cols-3 gap-2.5 mb-4">
            {/* mutually exclusive: VAT-included, VAT-on-top, or neither (no VAT) */}
            <Switch on={form.VAT_include} onChange={(v) => set({ VAT_include: v, VAT_add: false })} label="รวม VAT แล้ว" hint="ราคาที่กรอกรวม VAT" />
            <Switch on={form.VAT_add && !form.VAT_include} onChange={(v) => set({ VAT_add: v, VAT_include: false })} label="มี VAT 7%" />
            <Switch on={form.WHT_add} onChange={(v) => set({ WHT_add: v })} label="หัก ณ ที่จ่าย" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="ราคา / ยอดก่อนหักลด">
              <input
                type="text" readOnly disabled value={fmtMoney(priceBase)}
                title="คำนวณอัตโนมัติจากผลรวมรายการซื้อ — แก้ที่แท็บ “รายการซื้อ”"
                className={`${FIELD} text-right tabular-nums font-semibold !bg-slate-50 !text-slate-600 !border-slate-200 cursor-not-allowed`}
              />
              <span className="mt-1 block text-[11px] text-slate-400">= ผลรวมรายการซื้อ (แก้ที่แท็บรายการซื้อ)</span>
            </Field>
            <Field label="ส่วนลด">
              <input type="number" inputMode="decimal" value={form.discount} onChange={(e) => set({ discount: e.target.value })} placeholder="0.00" className={`${FIELD} text-right tabular-nums`} />
            </Field>
          </div>

          {form.WHT_add && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="อัตราหัก ณ ที่จ่าย">
                <select
                  value={form.WHT_percent}
                  onChange={(e) => {
                    const pct = Number(e.target.value)
                    const preset = WHT_PRESETS.find((w) => w.percent === pct)
                    set({ WHT_percent: pct, WHT_type: preset ? preset.type : form.WHT_type })
                  }}
                  className={FIELD}
                >
                  {WHT_PRESETS.map((w) => <option key={w.percent} value={w.percent}>{w.label}</option>)}
                </select>
              </Field>
              <Field label="ประเภทเงินได้">
                <input value={form.WHT_type} onChange={(e) => set({ WHT_type: e.target.value })} placeholder="เช่น ค่าบริการ" className={FIELD} />
              </Field>
            </div>
          )}

          {/* Computed breakdown */}
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100 text-[13px]">
            <Row label="ราคาก่อน VAT" value={amount.total_price} />
            <Row label={`VAT 7%`} value={amount.VAT} muted={!form.VAT_add} />
            <Row label="ราคาหลัง VAT" value={amount.total_price_afterVAT} />
            {form.WHT_add && <Row label={`หัก ณ ที่จ่าย ${amount.WHT_percent}%`} value={-amount.WHT} negative />}
          </div>

          <div className="mt-3 rounded-xl bg-brand-600 text-white px-4 py-3.5 flex items-center justify-between">
            <span className="text-[13px] font-medium text-brand-50">ยอดชำระรวม</span>
            <span className="text-[24px] font-bold tabular-nums leading-none">{fmtMoney(amount.payment)}</span>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <SectionHead icon={FileText} title="หมายเหตุ" />
          <textarea value={form.remark} onChange={(e) => set({ remark: e.target.value })} rows={4} placeholder="หมายเหตุเพิ่มเติม…" className={`${FIELD} resize-none`} />
        </section>
      </div>

      <SellerEditor
        open={editSeller != null}
        sellerId={editSeller}
        onClose={() => setEditSeller(null)}
        onSaved={(s) => set({
          seller_name: s.name ?? form.seller_name,
          seller_type: s.type ?? form.seller_type,
          seller_taxid: s.taxid ?? form.seller_taxid,
          seller_phone: s.phone ?? form.seller_phone,
          seller_address: s.address ?? form.seller_address,
        })}
      />
    </div>
  )
}

function Row({ label, value, muted, negative }) {
  return (
    <div className="flex items-center justify-between px-3.5 py-2">
      <span className={muted ? 'text-slate-400' : 'text-slate-500'}>{label}</span>
      <span className={`tabular-nums font-medium ${negative ? 'text-rose-600' : muted ? 'text-slate-400' : 'text-slate-800'}`}>{fmtMoney(value)}</span>
    </div>
  )
}

/* Money input: shows X,XXX.XX at rest, the raw number while editing. */
function MoneyInput({ value, onChange, className }) {
  const [focused, setFocused] = useState(false)
  const [raw, setRaw] = useState('')
  const has = value !== '' && value != null
  const display = focused ? raw : (has ? fmtMoney(value) : '')
  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={() => { setRaw(has ? String(value) : ''); setFocused(true) }}
      onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ''); setRaw(v); onChange(v) }}
      onBlur={() => setFocused(false)}
      className={className}
    />
  )
}

/* ── Billing tab ───────────────────────────────────────────────────────────── */
function BillingTab({ billing, setBilling, billSum, defaultDetail = '', defaultDate = '' }) {
  function addLine() {
    setBilling((b) => [...b, {
      // date & first-line detail seed from the expense header (both editable)
      date: defaultDate || toISOToday(),
      detail: b.length === 0 ? (defaultDetail || '') : '',
      N: 1, price: 0, price_total: 0, ref: '', unique_id: uid(),
    }])
  }
  // `price_total` (รวม) is what the user types; ราคา/หน่วย is derived (รวม ÷ จำนวน).
  function update(i, patch) {
    setBilling((b) => b.map((row, idx) => {
      if (idx !== i) return row
      const next = { ...row, ...patch }
      const N = Number(next.N) || 0
      const total = Number(next.price_total) || 0
      next.price = N ? Math.round((total / N + Number.EPSILON) * 100) / 100 : 0
      return next
    }))
  }
  function remove(i) { setBilling((b) => b.filter((_, idx) => idx !== i)) }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-4">
        <SectionHead icon={ShoppingCart} title="รายการซื้อ (Billing)" />
        <button onClick={addLine} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-brand-700"><Plus size={14} /> เพิ่มรายการ</button>
      </div>

      {!billing.length ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-[13px] text-slate-400">ยังไม่มีรายการซื้อ — กด “เพิ่มรายการ”</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-2 font-medium">วันที่</th>
                <th className="py-2 px-2 font-medium">รายการ</th>
                <th className="py-2 px-2 font-medium text-center w-28">จำนวน</th>
                <th className="py-2 px-2 font-medium text-right w-28">ราคา/หน่วย</th>
                <th className="py-2 px-2 font-medium text-right w-36">รวม</th>
                <th className="py-2 pl-2 font-medium w-48">อ้างอิง</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {billing.map((row, i) => (
                <tr key={row.unique_id || i} className="border-b border-slate-50">
                  <td className="py-1.5 pr-2"><input type="date" value={(row.date || '').slice(0, 10)} onChange={(e) => update(i, { date: e.target.value })} className={`${FIELD} !py-1.5 !px-2 text-[12.5px]`} /></td>
                  <td className="py-1.5 px-2"><input value={row.detail || ''} onChange={(e) => update(i, { detail: e.target.value })} placeholder="รายการ" className={`${FIELD} !py-1.5 !px-2`} /></td>
                  <td className="py-1.5 px-2">
                    <div className="flex items-center justify-center gap-1">
                      <button type="button" onClick={() => update(i, { N: Math.max(0, (Number(row.N) || 0) - 1) })}
                        className="w-6 h-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0"><Minus size={13} /></button>
                      <input inputMode="numeric" value={row.N ?? ''} onChange={(e) => update(i, { N: e.target.value.replace(/[^0-9]/g, '') })}
                        className={`${FIELD} !py-1.5 !px-1 w-10 text-center tabular-nums`} />
                      <button type="button" onClick={() => update(i, { N: (Number(row.N) || 0) + 1 })}
                        className="w-6 h-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0"><Plus size={13} /></button>
                    </div>
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-500">{fmtMoney(row.price)}</td>
                  <td className="py-1.5 px-2"><MoneyInput value={row.price_total} onChange={(v) => update(i, { price_total: v })} className={`${FIELD} !py-1.5 !px-2 text-right tabular-nums font-medium`} /></td>
                  <td className="py-1.5 pl-2"><input value={row.ref || ''} onChange={(e) => update(i, { ref: e.target.value })} className={`${FIELD} !py-1.5 !px-2`} /></td>
                  <td className="py-1.5"><button onClick={() => remove(i)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="py-2.5 pr-2 text-right text-[13px] text-slate-500">รวมรายการซื้อ</td>
                <td className="py-2.5 px-2 text-right text-[14px] font-semibold text-slate-900 tabular-nums">{fmtMoney(billSum)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

/* ── Files tab ─────────────────────────────────────────────────────────────── */
function FilesTab({ files, setFiles, form, onScan, ocrBusy, ocrDone, ocrError }) {
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState(null)
  const inputRef = useRef(null)
  const scannable = (f) => /pdf|image/i.test(f?.type || '') || /\.(pdf|png|jpe?g|webp|gif)$/i.test(f?.name || '')

  async function onFiles(list) {
    const arr = Array.from(list || [])
    if (!arr.length) return
    setUploading(true); setErr(null)
    const ym = periodFolder(form.date_buy)
    try {
      for (const f of arr) {
        const entry = await uploadExpenseFile(f, ym)
        setFiles((cur) => [...cur, entry])
      }
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function remove(i) { setFiles((cur) => cur.filter((_, idx) => idx !== i)) }
  function setNote(i, note) { setFiles((cur) => cur.map((f, idx) => idx === i ? { ...f, note } : f)) }
  function setTags(i, tags) { setFiles((cur) => cur.map((f, idx) => idx === i ? { ...f, tag: tags } : f)) }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <SectionHead icon={FileText} title="ไฟล์แนบ (ใบเสร็จ / ใบกำกับภาษี)" />

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files) }}
        onClick={() => inputRef.current?.click()}
        className="rounded-xl border border-dashed border-slate-200 hover:border-brand-300 hover:bg-brand-50/30 transition cursor-pointer py-7 text-center"
      >
        <input
          ref={inputRef} type="file" multiple accept="image/*,application/pdf,.pdf"
          className="hidden" onChange={(e) => onFiles(e.target.files)}
        />
        {uploading
          ? <div className="text-[13px] text-slate-500"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังอัปโหลด…</div>
          : <div className="text-slate-500">
              <Upload size={22} className="mx-auto mb-2 text-slate-400" />
              <div className="text-[13.5px] font-medium">คลิกหรือลากไฟล์มาวางที่นี่</div>
              <div className="text-[12px] text-slate-400 mt-0.5">รองรับรูปภาพและ PDF — กด “อ่านอัตโนมัติ” เพื่อให้ระบบกรอกให้</div>
            </div>}
      </div>
      {err && <div className="mt-2 text-[12.5px] text-rose-600 flex items-center gap-1"><AlertCircle size={14} /> {err}</div>}
      {ocrError && <div className="mt-2 text-[12.5px] text-rose-600 flex items-start gap-1"><AlertCircle size={14} className="mt-0.5 flex-none" /> <span className="break-words">{ocrError}</span></div>}

      {!files.length && !uploading ? (
        <div className="mt-3 text-center text-[12.5px] text-slate-400 py-2">ยังไม่มีไฟล์แนบ</div>
      ) : (
        <div className="mt-3 divide-y divide-slate-100">
          {files.map((f, i) => (
            <div key={f.uuid || f.location || i} className="flex items-center gap-3 py-2.5 px-1 rounded-lg hover:bg-slate-50/70 transition-colors">
              <a href={fileHref(f)} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                {f.thumbnail
                  ? <img src={f.thumbnail} alt="" className="w-full h-full object-cover" />
                  : isImage(f) && fileHref(f)
                    ? <img src={fileHref(f)} alt="" className="w-full h-full object-cover" />
                    : <Paperclip size={16} className="text-slate-400" />}
              </a>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <a href={fileHref(f)} target="_blank" rel="noreferrer" className="block text-[13.5px] font-medium text-slate-700 truncate hover:text-brand-600">{f.name || f.location}</a>
                  {onScan && f.location && scannable(f) && (
                    <ScanButton
                      busy={ocrBusy === f.location}
                      done={ocrDone?.has(f.location)}
                      onClick={() => onScan(f.location)}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <TagPicker value={f.tag} onChange={(tags) => setTags(i, tags)} />
                  <input
                    value={f.note || ''}
                    onChange={(e) => setNote(i, e.target.value)}
                    placeholder="โน้ต…"
                    className="flex-1 min-w-0 rounded-md bg-slate-50 px-2 py-1 text-[12px] text-slate-600 outline-none placeholder:text-slate-300 focus:bg-white focus:ring-1 focus:ring-brand-300 transition"
                  />
                </div>
              </div>
              <span className="text-[11.5px] text-slate-400 tabular-nums shrink-0">{fmtSize(f.size)}</span>
              <button onClick={() => remove(i)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── multi-select document-type tag picker ─────────────────────────────────── */
function TagPicker({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  const sel = Array.isArray(value) ? value : []
  const toggle = (t) => onChange(sel.includes(t) ? sel.filter((x) => x !== t) : [...sel, t])
  const label = sel.length === 0 ? 'ประเภทเอกสาร…' : sel.join(', ')
  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12px] ${sel.length ? 'border-brand-200 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-400'}`}
      >
        <span className="whitespace-normal text-left">{label}</span>
        <ChevronDown size={12} className="shrink-0 opacity-60" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-52 rounded-lg border border-slate-200 bg-white shadow-lg p-1">
          {FILE_TAGS.map((t) => {
            const on = sel.includes(t)
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggle(t)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-slate-50"
              >
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-brand-600 border-brand-600' : 'border-slate-300'}`}>
                  {on && <Check size={10} className="text-white" strokeWidth={3} />}
                </span>
                <span className="text-slate-700">{t}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── utils ─────────────────────────────────────────────────────────────────── */
function toISOToday() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function uid() {
  try { return crypto.randomUUID() } catch { return 'x' + Math.abs(Date.now()).toString(36) }
}
function isImage(f) { return /^image\//.test(f?.type || '') || /\.(png|jpe?g|gif|webp)$/i.test(f?.name || '') }
// Files live in a private Wasabi bucket — never link the raw object URL (it 403s
// "Public use of objects is not allowed"). Serve through the auth-gated proxy,
// which signs a short-lived presigned redirect. Falls back to any stored url.
function fileHref(f) {
  if (f?.location) return '/api/expense-file/' + String(f.location).split('/').map(encodeURIComponent).join('/')
  return f?.url || ''
}
function fmtSize(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) return n + ' B'
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB'
  return (n / 1024 / 1024).toFixed(1) + ' MB'
}
